
/**
 * BookingMethodSelectionModal - Allows users to choose between booking flows
 * 
 * This modal appears when a user clicks "Book It Now" and allows them to select:
 * - API Flow: Custom booking flow using BookingModal
 * - Whitelabel Flow: Embedded Nuitee whitelabel iframe using WhitelabelIframeModal
 */

import React from 'react';
import ModalOverlay from '../ModalOverlay/ModalOverlay';
import { log } from '@/utils/logger';
import './BookingMethodSelectionModal.css';

interface BookingMethodSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAPI: () => void;
  onSelectWhitelabel: () => void;
  hotelName?: string;
}

const BookingMethodSelectionModal: React.FC<BookingMethodSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectAPI,
  onSelectWhitelabel,
  hotelName,
}) => {
  if (!isOpen) return null;

  log('[BookingMethodSelectionModal] Rendering', {
    isOpen,
    hotelName,
  });

  const handleSelectAPI = () => {
    log('[BookingMethodSelectionModal] API flow selected');
    onSelectAPI();
  };

  const handleSelectWhitelabel = () => {
    log('[BookingMethodSelectionModal] Whitelabel flow selected');
    onSelectWhitelabel();
  };

  return (
    <ModalOverlay onClose={onClose} animate>
      <div className="booking-method-selection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="booking-method-selection-modal-header">
          <h2 className="booking-method-selection-modal-title">
            {hotelName ? `Book ${hotelName}` : 'Choose Booking Method'}
          </h2>
          <button
            className="booking-method-selection-modal-close"
            onClick={onClose}
            aria-label="Close booking method selection"
          >
            ×
          </button>
        </div>

        <div className="booking-method-selection-modal-content">
          <p className="booking-method-selection-modal-description">
            Select your preferred booking method:
          </p>

          <div className="booking-method-selection-modal-options">
            <button
              className="booking-method-selection-modal-option"
              onClick={handleSelectAPI}
            >
              <div className="booking-method-selection-modal-option-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="booking-method-selection-modal-option-content">
                <h3 className="booking-method-selection-modal-option-title">API Flow</h3>
                <p className="booking-method-selection-modal-option-description">
                  Custom booking experience with step-by-step flow
                </p>
              </div>
              <div className="booking-method-selection-modal-option-arrow">
                →
              </div>
            </button>

            <button
              className="booking-method-selection-modal-option"
              onClick={handleSelectWhitelabel}
            >
              <div className="booking-method-selection-modal-option-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M3 9H21" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 3V21" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <div className="booking-method-selection-modal-option-content">
                <h3 className="booking-method-selection-modal-option-title">Whitelabel</h3>
                <p className="booking-method-selection-modal-option-description">
                  Full-featured Nuitee booking experience
                </p>
              </div>
              <div className="booking-method-selection-modal-option-arrow">
                →
              </div>
            </button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
};

export default BookingMethodSelectionModal;
